import { HORSE_BREEDS, HORSE_COLORS, RacingCountryCodeSchema, } from "../horses/horse-details.js";
/** The `ExtractionKind`s the horse-add scan flow uses
 * (`docs/products/stable.md` "Adding a horse"). */
export const HORSE_SCAN_KINDS = ["HORSE_PASSPORT", "HORSE_NAME_PLATE"];
export function isHorseScanKind(kind) {
    return HORSE_SCAN_KINDS.includes(kind);
}
/** Every Horse Details field the scan can prefill — mirrors
 * `horseFieldsSchema` (`packages/stable/src/validation.ts`) with
 * `dateOfBirth` flattened into its three stored columns, exactly like the
 * `horses` table itself (`packages/database/src/schema.ts`). Excludes
 * `hemisphereOverride`: a deliberate manual override a scan never infers. */
export const HORSE_SCAN_FIELD_NAMES = [
    "name",
    "breed",
    "breedOtherLabel",
    "sex",
    "dobYear",
    "dobMonth",
    "dobDay",
    "passportNumber",
    "sire",
    "dam",
    "bredIn",
    "color",
    "barnNumber",
];
/** Fields the confirm screen must block save on until present
 * (`docs/products/stable.md` "Horse Details": "Compulsory fields, requested
 * first: Name; Breed...; Sex...; Date of Birth (year required...)"). */
export const HORSE_SCAN_COMPULSORY_FIELD_NAMES = [
    "name",
    "breed",
    "sex",
    "dobYear",
];
const NO_VALUE = { value: null, confidence: null };
const HORSE_SCAN_SEXES = ["COLT", "FILLY", "MARE", "STALLION"];
function normalizeEnumGuess(raw, candidates) {
    const upper = raw.toUpperCase();
    return candidates.find((candidate) => candidate === upper) ?? null;
}
/**
 * Best-effort normalization of one provider-returned free-text guess into
 * this field's actual stored shape. A guess that cannot be confidently
 * mapped is treated as not extracted (`null`) rather than passed through as
 * an invalid raw string — the confirm screen then shows that field as
 * needing manual entry instead of pre-selecting something wrong, matching
 * "each field individually editable" without silently trusting OCR text.
 *
 * `sex` deliberately never maps to `GELDING`: gelding is a dated one-way
 * lifecycle event recorded separately, never set on add
 * (`docs/products/stable.md`; `horseFieldsSchema` rejects it outright).
 */
function normalizeField(field, raw) {
    if (raw === undefined || raw.value === null)
        return null;
    const trimmed = raw.value.trim();
    if (trimmed.length === 0)
        return null;
    switch (field) {
        case "breed":
            return normalizeEnumGuess(trimmed, HORSE_BREEDS);
        case "sex":
            return normalizeEnumGuess(trimmed, HORSE_SCAN_SEXES);
        case "color":
            return normalizeEnumGuess(trimmed, HORSE_COLORS);
        case "bredIn": {
            const parsed = RacingCountryCodeSchema.safeParse(trimmed);
            return parsed.success ? parsed.data : null;
        }
        case "dobYear":
        case "dobMonth":
        case "dobDay": {
            const numeric = Number.parseInt(trimmed, 10);
            return Number.isFinite(numeric) ? String(numeric) : null;
        }
        default:
            return trimmed;
    }
}
/**
 * Builds the confirm-screen draft fields from a provider's raw guesses:
 * normalizes each recognizable value, and enforces that Passport Number is
 * only ever carried from a passport scan, never a name-plate scan
 * (`docs/products/stable.md`: "Passport Number (only captured via
 * passport-image scan, not name-plate scan)") even if the model returned one
 * anyway.
 */
export function buildHorseScanFields(kind, raw) {
    const result = {};
    for (const field of HORSE_SCAN_FIELD_NAMES) {
        if (field === "passportNumber" && kind !== "HORSE_PASSPORT") {
            result[field] = NO_VALUE;
            continue;
        }
        const rawField = raw[field];
        const normalized = normalizeField(field, rawField);
        result[field] =
            normalized === null
                ? NO_VALUE
                : { value: normalized, confidence: rawField?.confidence ?? null };
    }
    return result;
}
/** Which compulsory fields the confirm screen must still block save on —
 * mirrors `horseFieldsSchema`'s own compulsory rule that Breed additionally
 * requires `breedOtherLabel` once it resolves to `OTHER`. */
export function missingCompulsoryHorseScanFields(fields) {
    const missing = HORSE_SCAN_COMPULSORY_FIELD_NAMES.filter((field) => fields[field].value === null);
    if (fields.breed.value === "OTHER" && fields.breedOtherLabel.value === null) {
        missing.push("breedOtherLabel");
    }
    return missing;
}
/**
 * Flattens the fields a horse was actually saved with into the same shape as
 * `HorseScanFields`' values, so the persisted extraction record can hold
 * both what the scan found and what was actually confirmed side by side
 * (`ARCHITECTURE.md` §11.12: "Persist extraction provider/version, source
 * file reference, confidence/provenance where available, review state, and
 * user corrections").
 */
export function horseFieldsToScanSnapshot(fields) {
    return {
        name: fields.name,
        breed: fields.breed,
        breedOtherLabel: fields.breedOtherLabel,
        sex: fields.sex,
        dobYear: String(fields.dateOfBirth.year),
        dobMonth: fields.dateOfBirth.month === null
            ? null
            : String(fields.dateOfBirth.month),
        dobDay: fields.dateOfBirth.day === null ? null : String(fields.dateOfBirth.day),
        passportNumber: fields.passportNumber,
        sire: fields.sire,
        dam: fields.dam,
        bredIn: fields.bredIn,
        color: fields.color,
        barnNumber: fields.barnNumber,
    };
}
